import { z } from 'zod';
import { dateStringSchema } from '../dateString.js';
export const paymentStatusSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('teoOnly'), internalId: z.string() }),
    z.object({
        kind: z.literal('scheduledAccounting'),
        externalId: z.string(),
        internalId: z.string(),
    }),
    z.object({
        kind: z.literal('dueAccounting'),
        externalId: z.string(),
        internalId: z.string(),
        dueDate: dateStringSchema,
        invoiceId: z.string(),
    }),
    z.object({
        kind: z.literal('payments'),
        externalId: z.string(),
        externalStatus: z.string(),
        internalId: z.string(),
        invoiceId: z.string().nullable(),
    }),
]);
export const reconciliationReasonSchema = z.enum([
    'OK',
    'NO_QBO_INVOICE',
    'QBO_TOTAL_MISMATCH',
    'QBO_BALANCE_OUTSTANDING',
    'PAYABLI_AMOUNT_MISMATCH',
    'PAYABLI_REVERSED',
    'PAYABLI_UNSETTLED',
]);
export const payabliPostingStatusSchema = z.enum(['pending', 'posted', 'failed', 'blocked']);
export const qboUnapplyMarkerKindSchema = z.enum([
    'removed',
    'reduced',
    'never_posted',
    'required',
    'failed',
    'pending',
]);
export const QBO_UNAPPLY_OUTCOME = {
    removed: 'removed',
    reduced: 'reduced',
    neverPosted: 'never_posted',
    failed: 'failed',
    pending: 'pending',
};
export const QBO_UNAPPLY_REASON_MARKERS = {
    removed: 'qbo_unapply_removed',
    reduced: 'qbo_unapply_reduced',
    never_posted: 'qbo_unapply_never_posted',
    required: 'qbo_unapply_required',
    failed: 'qbo_unapply_failed',
    pending: 'qbo_unapply_pending',
};
export function qboUnapplyReason(reversalReason, outcome) {
    return `${reversalReason}: ${QBO_UNAPPLY_REASON_MARKERS[outcome]}`;
}
export function qboUnapplyMarkerFromReason(reason) {
    if (reason === null)
        return null;
    return (qboUnapplyMarkerKindSchema.options.find((kind) => reason.includes(QBO_UNAPPLY_REASON_MARKERS[kind])) ?? null);
}
export const hppReconcileKeySupportSchema = z.enum(['supported', 'missing', 'unknown']);
export const payabliFeeCredentialSchema = z.object({
    service: z.string(),
    fixed: z.number().nullable(),
    percent: z.number().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    absorb: z.boolean().nullable(),
    mode: z.number().nullable(),
    accountId: z.string().nullable(),
    referenceId: z.number().nullable(),
});
export const paypointFeeReadFailureReasonSchema = z.enum([
    'no_page',
    'unauthorized',
    'request_failed',
    'unreadable',
]);
export const payabliFeeCaptureSchema = z.object({
    capturedAt: z.string(),
    credentials: z.array(payabliFeeCredentialSchema),
});
export const HPP_RECONCILE_KEY_REMEDY = 'Declare an invoiceNumber payor field on this payment page in Payabli, then run Refresh Paypoint. Until then, payments taken through it arrive with nothing naming an invoice and have to be matched by hand.';
export const CURRENCY_EPSILON = 0.005;
export const payabliPaymentStatusSchema = z.enum([
    'APPROVED',
    'SETTLED',
    'PENDING',
    'FAILED',
    'RETURNED',
    'REFUNDED',
    'VOIDED',
]);
export function paymentMoneyState(status) {
    switch (status) {
        case 'APPROVED':
        case 'SETTLED':
        case 'PENDING':
            return 'live';
        case 'RETURNED':
        case 'REFUNDED':
        case 'VOIDED':
            return 'reversed';
        case 'FAILED':
            return 'declined';
        default: {
            const _exhaustive = status;
            throw new Error(`Unhandled Payabli payment status: ${String(_exhaustive)}`);
        }
    }
}
export function paymentStatusesIn(...states) {
    return payabliPaymentStatusSchema.options.filter((status) => states.includes(paymentMoneyState(status)));
}
export const payabliObservationSourceSchema = z.enum(['webhook', 'sweep']);
export const payabliPaymentMethodSchema = z.enum(['card', 'ach']);
export function payabliMoneySide(status, method) {
    switch (status) {
        case 'APPROVED':
        case 'SETTLED':
        case 'REFUNDED':
            return 'in';
        case 'PENDING':
            return method === 'ach' ? 'in' : 'none';
        case 'RETURNED':
        case 'VOIDED':
        case 'FAILED':
            return 'back';
        default: {
            const _exhaustive = status;
            throw new Error(`Unhandled Payabli payment status: ${String(_exhaustive)}`);
        }
    }
}
export function payabliReversedCents(payment) {
    switch (payment.status) {
        case 'RETURNED':
        case 'VOIDED':
        case 'FAILED':
            return payment.amountCents;
        case 'REFUNDED':
            return payment.refundedCents ?? payment.amountCents;
        case 'PENDING':
        case 'APPROVED':
        case 'SETTLED':
            return 0;
        default: {
            const _exhaustive = payment.status;
            throw new Error(`Unhandled Payabli payment status: ${String(_exhaustive)}`);
        }
    }
}
export function isReceiptablePayment(payment) {
    switch (payment.status) {
        case 'SETTLED':
        case 'REFUNDED':
            return true;
        case 'APPROVED':
            return payment.method === 'card';
        case 'PENDING':
        case 'FAILED':
        case 'RETURNED':
        case 'VOIDED':
            return false;
        default: {
            const _exhaustive = payment.status;
            throw new Error(`Unhandled PayabliPaymentStatusValue: ${String(_exhaustive)}`);
        }
    }
}
export const fundingStatusSchema = z.enum(['funded', 'pending', 'na']);
export const payabliBalancePushOutcomeSchema = z.enum([
    'updated',
    'unchanged',
    'not_found',
    'ambiguous',
    'balance_unknown',
]);
export const persistedPaymentStatusSchema = z.object({
    lifecycle: paymentStatusSchema,
    reconciliation: z.object({
        auto: z.boolean(),
        reason: reconciliationReasonSchema,
        isReconciled: z.boolean(),
    }),
});
export const qboClosureReasonSchema = z.enum(['paid', 'voided', 'deleted']);
export const teoInvoiceStatusSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('forecast') }),
    z.object({ kind: z.literal('open') }),
    z.object({
        kind: z.literal('partialPay'),
        balance: z.number(),
        total: z.number(),
    }),
    z.object({ kind: z.literal('paid') }),
    z.object({ kind: z.literal('voided') }),
    z.object({ kind: z.literal('deleted') }),
]);
export const DEPOSIT_EXPORT_MAX_RANGE_DAYS = 92;
export const PAYABLI_BACKFILL_MAX_DAYS = 730;
//# sourceMappingURL=paymentStatus.js.map